import { useState, useCallback, useMemo } from 'react';
import { FilterOptions, Client } from '../types/common';

const defaultFilters: FilterOptions = {
  search: '',
  clientStage: 'all',
  rollingWindow: 12,
  sortBy: 'revenue',
  sortDir: 'desc',
};

export const useFilters = (clients: Client[]) => {
  const [filters, setFilters] = useState<FilterOptions>(defaultFilters);

  const updateFilter = useCallback(<K extends keyof FilterOptions>(key: K, value: FilterOptions[K]) => {
    setFilters((prev: FilterOptions) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const filteredClients = useMemo(() => {
    let result = [...clients];

    // Search by name
    if (filters.search.trim()) {
      const query = filters.search.trim().toLowerCase();
      result = result.filter(c =>
        String(c.name).toLowerCase().includes(query) ||
        String(c.id).toLowerCase().includes(query) ||
        (c.region ?? '').toLowerCase().includes(query)
      );
    }

    if (filters.clientStage === 'new') {
      result = result.filter(c => c.isNew);
    } else if (filters.clientStage === 'recurring') {
      result = result.filter(c => c.isRecurring);
    } else if (filters.clientStage === 'existing') {
      result = result.filter(c => !c.isNew);
    }

    // Sort
    result.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (filters.sortBy) {
        case 'name':
          aVal = String(a.name).toLowerCase();
          bVal = String(b.name).toLowerCase();
          break;
        case 'revenue':
          aVal = a.revenue;
          bVal = b.revenue;
          break;
        case 'invoiced':
          aVal = a.invoiced;
          bVal = b.invoiced;
          break;
        case 'outstanding':
          aVal = a.outstanding;
          bVal = b.outstanding;
          break;
        case 'lastInvoice':
          aVal = a.lastInvoice ?? '';
          bVal = b.lastInvoice ?? '';
          break;
        default:
          aVal = a.revenue;
          bVal = b.revenue;
      }

      if (aVal < bVal) return filters.sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return filters.sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [clients, filters]);

  return { filters, updateFilter, resetFilters, filteredClients };
};
